import type { ReactNode } from 'react'
import type { IconComponent } from './icon.types'

/**
 * The app's button vocabulary, modelled on the Tailwind UI button set
 * (resources/tailwind-examples/application-ui/elements/buttons) but with every
 * colour read from a shell-* token instead of a literal `indigo-600` — the same
 * rule the nav and shell already follow, so one token block re-themes
 * everything.
 *
 * Purely presentational: no Presenter, no state. What the button *does* and
 * whether it is enabled are decisions a Presenter has already made and handed
 * over as props.
 */
export type ButtonTone = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md'

const TONE: Record<ButtonTone, string> = {
  primary: 'bg-shell-accent text-shell-accent-ink shadow-xs hover:bg-shell-accent-hover disabled:hover:bg-shell-accent',
  secondary:
    'bg-shell-surface text-shell-ink inset-ring inset-ring-shell-border shadow-xs hover:bg-shell-surface-muted disabled:hover:bg-shell-surface',
  danger:
    'bg-shell-surface text-shell-danger-ink inset-ring inset-ring-shell-border shadow-xs hover:bg-shell-danger-soft disabled:hover:bg-shell-surface',
  ghost: 'text-shell-ink-secondary hover:text-shell-ink hover:bg-shell-surface-muted',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'gap-x-1 px-2 py-1 text-xs',
  md: 'gap-x-1.5 px-3 py-2 text-sm',
}

const BASE =
  'inline-flex items-center justify-center rounded-md font-semibold whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

type Props = {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  tone?: ButtonTone
  size?: ButtonSize
  disabled?: boolean
  /** Rendered before the label; the 20/solid set suits button sizes. */
  icon?: IconComponent
  /** Screen-reader-only suffix, for a control whose visible label repeats down a list. */
  srSuffix?: string
  className?: string
}

export const Button = ({
  children,
  onClick,
  type = 'button',
  tone = 'secondary',
  size = 'md',
  disabled = false,
  icon: Icon,
  srSuffix,
  className = '',
}: Props) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`${BASE} ${SIZE[size]} ${TONE[tone]} ${className}`}
  >
    {Icon && <Icon className={size === 'sm' ? 'size-3.5 shrink-0' : 'size-4 shrink-0'} />}
    {children}
    {srSuffix && <span className="sr-only">, {srSuffix}</span>}
  </button>
)
